/*
 * Viretta – blog / recipe sections: shared behaviour (vbs-sections.js).
 * Loaded with <script defer> by every section that needs it; the guard below makes repeated tags harmless.
 * Replaces the inline <script> blocks of those sections (same behaviour) plus:
 *  - videos marked data-vbs-autoplay only download/play while on screen, pause off screen,
 *    and never autoplay with prefers-reduced-motion,
 *  - a small scroll-snap carousel (data-vbs-carousel) that replaces Swiper (same layout, bullets, arrows, mouse drag),
 *  - re-initialises a section after it is re-rendered in the theme editor (shopify:section:load).
 * Every section root has data-vbs="<name>" and is initialised once.
 */
(() => {
  if (window.VbsSections) return;

  const reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  const inits = {};
  const define = (name, fn) => { inits[name] = fn; };

  const onKeyActivate = (el, fn) => {
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(e); }
    });
  };

  /* ------------------------------------------------------------------ videos */
  const playSafe = (video) => {
    try {
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    } catch (e) { /* ignore */ }
  };

  // Muted loop videos that used to autoplay: play while visible, pause while not.
  const autoplayIO = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const video = entry.target;
      if (entry.isIntersecting) {
        if (!reduceMotion.matches && !video.vbsUserPaused && video.paused) playSafe(video);
      } else if (!video.paused) {
        video.vbsSelfPause = true;
        video.pause();
      }
    });
  }, { rootMargin: '150px 0px', threshold: 0.01 }) : null;

  const observeAutoplay = (video) => {
    if (video.vbsObserved) return;
    video.vbsObserved = true;
    video.muted = true;
    video.addEventListener('pause', () => {
      if (video.vbsSelfPause) { video.vbsSelfPause = false; return; }
      if (!video.ended) video.vbsUserPaused = true;
    });
    video.addEventListener('play', () => { video.vbsUserPaused = false; });
    if (autoplayIO) autoplayIO.observe(video);
    else if (!reduceMotion.matches) playSafe(video);
  };

  // Click-to-play buttons: <div data-vbs-play="video-id"> (chef section)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-vbs-play]');
    if (!btn) return;
    const video = document.getElementById(btn.dataset.vbsPlay);
    if (!video) return;
    playSafe(video);
    video.setAttribute('controls', 'true');
    btn.style.display = 'none';
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const btn = e.target.closest && e.target.closest('[data-vbs-play]');
    if (!btn) return;
    e.preventDefault();
    btn.click();
  });

  /* ------------------------------------------------------------------ carousel (Swiper replacement) */
  // Markup: <div class="swiper vbs-car" data-vbs-carousel> <div class="swiper-wrapper"> <div class="swiper-slide">…
  // Layout (slides per view, gap) lives in the section CSS (--vbs-per / --vbs-gap); this only adds
  // pagination bullets, prev/next, disabled states and mouse drag. Scrolling itself is native (scroll-snap).
  class Carousel {
    constructor(root, opts) {
      this.root = root;
      this.track = root.querySelector('.swiper-wrapper');
      this.opts = opts || {};
      this.listeners = [];
      this.positions = [0];
      this.index = 0;
      if (!this.track) return;
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.bindDrag();
      if ('ResizeObserver' in window) new ResizeObserver(() => this.update()).observe(this.track);
      window.addEventListener('resize', () => this.update());
      this.update();
    }

    slides() { return Array.from(this.track.children).filter((el) => el.classList.contains('swiper-slide')); }

    update() {
      const track = this.track;
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const base = track.getBoundingClientRect().left - track.scrollLeft;
      const pos = [];
      this.slides().forEach((slide) => {
        const x = Math.round(slide.getBoundingClientRect().left - base);
        if (x < max - 1) pos.push(Math.max(0, x));
      });
      if (!pos.length || pos[0] !== 0) pos.unshift(0);
      if (max > 1 && max - pos[pos.length - 1] > 1) pos.push(max);
      this.positions = pos;
      this.max = max;
      this.renderBullets();
      this.onScroll(true);
    }

    renderBullets() {
      const el = this.opts.pagination;
      if (!el) return;
      const count = this.positions.length;
      el.classList.add('swiper-pagination-bullets', 'swiper-pagination-horizontal', 'swiper-pagination-clickable');
      el.classList.toggle('swiper-pagination-lock', count <= 1);
      if (el.children.length === count) return;
      el.innerHTML = '';
      for (let i = 0; i < count; i += 1) {
        const b = document.createElement('span');
        b.className = 'swiper-pagination-bullet';
        b.setAttribute('role', 'button');
        b.setAttribute('tabindex', '0');
        b.setAttribute('aria-label', `Bild ${i + 1} anzeigen`);
        b.addEventListener('click', () => this.goTo(i));
        onKeyActivate(b, () => this.goTo(i));
        el.appendChild(b);
      }
    }

    nearest(x) {
      let best = 0;
      this.positions.forEach((p, i) => { if (Math.abs(p - x) < Math.abs(this.positions[best] - x)) best = i; });
      return best;
    }

    onScroll(force) {
      const x = this.track.scrollLeft;
      const i = x >= this.max - 1 ? this.positions.length - 1 : this.nearest(x);
      const changed = i !== this.index;
      this.index = i;
      this.isBeginning = x <= 1;
      this.isEnd = x >= this.max - 1;
      const el = this.opts.pagination;
      if (el) Array.from(el.children).forEach((b, n) => b.classList.toggle('swiper-pagination-bullet-active', n === i));
      if (changed || force) this.listeners.forEach((fn) => fn(this));
    }

    goTo(i) {
      const n = Math.max(0, Math.min(this.positions.length - 1, i));
      this.track.scrollTo({ left: this.positions[n], behavior: 'smooth' });
    }

    next() { this.goTo(this.index + 1); }

    prev() { this.goTo(this.index - 1); }

    onChange(fn) { this.listeners.push(fn); fn(this); }

    // Mouse drag like Swiper's simulateTouch (touch and trackpads scroll natively)
    bindDrag() {
      const track = this.track;
      let startX = 0; let startLeft = 0; let dragging = false; let moved = false; let startIndex = 0;
      track.addEventListener('dragstart', (e) => e.preventDefault());
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        if (e.target.closest('button, input, select, textarea, form, .bs-swatch-element')) return;
        dragging = true; moved = false;
        startX = e.clientX; startLeft = track.scrollLeft; startIndex = this.index;
      });
      window.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 5) {
          moved = true;
          track.style.scrollSnapType = 'none';
          track.style.scrollBehavior = 'auto';
        }
        if (moved) track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', (e) => {
        if (!dragging) return;
        dragging = false;
        if (!moved) return;
        const dx = e.clientX - startX;
        let target = this.nearest(track.scrollLeft);
        if (target === startIndex && Math.abs(dx) > 30) target = startIndex + (dx < 0 ? 1 : -1);
        track.style.scrollSnapType = '';
        track.style.scrollBehavior = '';
        this.goTo(target);
        // swallow the click that follows a drag
        const stop = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
        track.addEventListener('click', stop, { capture: true, once: true });
        setTimeout(() => track.removeEventListener('click', stop, { capture: true }), 50);
      });
    }
  }

  /* ------------------------------------------------------------------ helpers */
  const $ = (root, sel) => root.querySelector(sel);
  const $$ = (root, sel) => Array.from(root.querySelectorAll(sel));

  /* ================================================================== sections */
  /* ---------------- recipe-section-slider (video recipe tabs) ---------------- */
  define('rss', (section) => {
    // 1–3. play / mute / save (delegated, so tracks inserted later work too)
    section.addEventListener('click', (e) => {
      const play = e.target.closest('.js-play-video-btn');
      if (play) {
        e.preventDefault(); e.stopPropagation();
        const video = play.closest('.vr-slider-media-wrap').querySelector('.js-featured-video');
        if (video) {
          video.setAttribute('controls', 'true');
          playSafe(video);
          play.style.display = 'none';
          video.addEventListener('ended', () => {
            video.removeAttribute('controls');
            video.currentTime = 0;
            play.style.display = 'flex';
          }, { once: true });
        }
        return;
      }
      const mute = e.target.closest('.js-mute-video-btn');
      if (mute) {
        e.preventDefault(); e.stopPropagation();
        const video = mute.closest('.vr-slider-media-wrap').querySelector('.js-featured-video');
        if (video) {
          video.muted = !video.muted;
          mute.querySelector('.icon-mute').style.display = video.muted ? 'block' : 'none';
          mute.querySelector('.icon-unmute').style.display = video.muted ? 'none' : 'block';
        }
        return;
      }
      const save = e.target.closest('.js-save-btn');
      if (save) {
        e.preventDefault(); e.stopPropagation();
        save.classList.toggle('active');
      }
    });

    // 4. tabs, colour variables, dots, arrows
    const tabBtns = $$(section, '.js-tab-btn');
    const dotsContainer = $(section, '.js-vr-dots');
    const mobileControls = $(section, '.vr-slider-mobile-controls');
    const deskArrows = $(section, '.vr-slider-desktop-arrows');
    let currentTrack = $(section, '.js-vr-track.active');
    const bound = new WeakSet();

    const updateSliderDots = () => {
      if (!dotsContainer || !currentTrack) return;
      dotsContainer.innerHTML = '';
      dotsContainer.style.transform = '';
      const cards = currentTrack.querySelectorAll('.vr-slider-card');
      if (cards.length <= 1) {
        if (mobileControls) mobileControls.style.display = 'none';
        if (deskArrows) deskArrows.style.display = 'none';
        return;
      }
      if (mobileControls) mobileControls.style.display = '';
      if (deskArrows) deskArrows.style.display = '';
      cards.forEach((card, index) => {
        const dot = document.createElement('div');
        dot.classList.add('dot');
        if (index === 0) dot.classList.add('active');
        dot.addEventListener('click', () => {
          currentTrack.scrollTo({ left: card.offsetLeft - currentTrack.offsetLeft, behavior: 'smooth' });
        });
        dotsContainer.appendChild(dot);
      });
    };

    // Active dot + sliding dot window (5 visible dots)
    const bindScroll = (track) => {
      if (bound.has(track)) return;
      bound.add(track);
      track.addEventListener('scroll', () => {
        if (track !== currentTrack) return;
        const cards = track.querySelectorAll('.vr-slider-card');
        const dots = dotsContainer.querySelectorAll('.dot');
        const trackLeft = track.getBoundingClientRect().left;
        let activeIndex = 0;
        let minDistance = Infinity;
        cards.forEach((card, index) => {
          const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
          if (distance < minDistance) { minDistance = distance; activeIndex = index; }
        });
        dots.forEach((dot) => dot.classList.remove('active'));
        if (dots[activeIndex]) {
          dots[activeIndex].classList.add('active');
          const step = 16; // 8px dot + 8px gap
          const totalDots = dots.length;
          if (totalDots > 5) {
            let translateX = 0;
            if (activeIndex <= 2) translateX = 0;
            else if (activeIndex >= totalDots - 2) translateX = (totalDots - 5) * step;
            else translateX = (activeIndex - 2) * step;
            dotsContainer.style.transform = `translateX(-${translateX}px)`;
          }
        }
      }, { passive: true });
    };

    if (currentTrack) bindScroll(currentTrack);
    updateSliderDots();

    $$(section, '.js-vr-prev').forEach((btn) => btn.addEventListener('click', () => {
      if (!currentTrack || !currentTrack.querySelectorAll('.vr-slider-card').length) return;
      currentTrack.scrollBy({ left: -currentTrack.offsetWidth, behavior: 'smooth' });
    }));
    $$(section, '.js-vr-next').forEach((btn) => btn.addEventListener('click', () => {
      if (!currentTrack || !currentTrack.querySelectorAll('.vr-slider-card').length) return;
      currentTrack.scrollBy({ left: currentTrack.offsetWidth, behavior: 'smooth' });
    }));

    const trackById = (id) => {
      let track = section.querySelector(`.js-vr-track[id="${id}"]`);
      if (!track) {
        const tpl = section.querySelector(`template.js-vr-track-tpl[data-target="${id}"]`);
        if (tpl) {
          tpl.parentNode.insertBefore(tpl.content.cloneNode(true), tpl.nextSibling);
          track = section.querySelector(`.js-vr-track[id="${id}"]`);
        }
      }
      return track;
    };

    // Tab click – swaps the track AND the colour variables
    tabBtns.forEach((btn) => btn.addEventListener('click', () => {
      tabBtns.forEach((t) => t.classList.remove('active'));
      btn.classList.add('active');
      const d = btn.dataset;
      section.style.setProperty('--vr-bg', d.bg);
      section.style.setProperty('--vr-border', d.border);
      section.style.setProperty('--vr-title', d.title);
      section.style.setProperty('--vr-tab-inactive', d.inactive);
      section.style.setProperty('--vr-card-title', d.cardTitle);
      section.style.setProperty('--vr-badge-min-bg', d.badge1Bg);
      section.style.setProperty('--vr-badge-min-text', d.badge1Text);
      section.style.setProperty('--vr-badge-level-bg', d.badge2Bg);
      section.style.setProperty('--vr-badge-level-text', d.badge2Text);

      const target = trackById(d.target);
      $$(section, '.js-vr-track').forEach((track) => {
        if (track === target) {
          track.classList.add('active');
          currentTrack = track;
          currentTrack.scrollLeft = 0;
          bindScroll(track);
        } else {
          track.classList.remove('active');
        }
      });
      updateSliderDots();
    }));
  });

  /* ---------------- recipe-listing (load more + page numbers without reload) ---------------- */
  // Was: fetch() of the WHOLE next page (~1 MB HTML). Now: Shopify Section Rendering API (?section_id=…),
  // which returns only this section's HTML. Falls back to normal navigation if the request fails.
  define('rl', (sectionEl) => {
    const sectionId = sectionEl.dataset.sectionId;
    const container = document.getElementById(`AjaxContainer-${sectionId}`);
    if (!container) return;

    const sectionUrl = (url) => {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', sectionId);
      return u.toString();
    };

    const fetchContent = (url, isAppend) => {
      if (!url) return;
      container.style.opacity = '0.5';
      fetch(sectionUrl(url))
        .then((response) => { if (!response.ok) throw new Error(response.status); return response.text(); })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newGrid = doc.getElementById(`RecipeGrid-${sectionId}`);
          const newFooter = doc.getElementById(`PaginationFooter-${sectionId}`);
          const newContainer = doc.getElementById(`AjaxContainer-${sectionId}`);
          if (!newGrid || !newFooter || !newContainer) throw new Error('section not found');
          if (isAppend) {
            const currentGrid = container.querySelector('.recipe-grid');
            newGrid.querySelectorAll('.recipe-card').forEach((item) => currentGrid.appendChild(item));
            container.querySelector('.recipe-pagination-footer').innerHTML = newFooter.innerHTML;
          } else {
            container.innerHTML = newContainer.innerHTML;
          }
          container.style.opacity = '1';
        })
        .catch(() => { window.location.href = url; });
    };

    // Delegated, so the buttons/links inserted by AJAX keep working without re-binding
    container.addEventListener('click', (e) => {
      const more = e.target.closest('.load-more-btn');
      if (more) {
        e.preventDefault();
        fetchContent(more.getAttribute('data-next-url'), true);
        return;
      }
      const link = e.target.closest('.ajax-nav-link');
      if (link) {
        e.preventDefault();
        fetchContent(link.getAttribute('href'), false);
        const sec = document.getElementById(`RecipeSection-${sectionId}`);
        if (sec) sec.scrollIntoView({ behavior: 'smooth' });
      }
    });
  });

  /* ---------------- recipe-hero (video overlay, sticky mini video, portions) ---------------- */
  const playSVG = '<svg viewBox="0 0 60 60" width="40" height="40"><path d="M41 30L25 39.2372V20.7628L41 30Z" fill="white"/></svg>';
  const pauseSVG = '<svg viewBox="0 0 60 60" width="40" height="40"><rect x="22" y="20" width="6" height="20" fill="white"/><rect x="32" y="20" width="6" height="20" fill="white"/></svg>';

  define('rh', (section) => {
    $$(section, '.video-wrapper').forEach((wrapper) => {
      const video = wrapper.querySelector('.recipe-video-element');
      const overlay = wrapper.querySelector('.play-overlay');
      if (!video || !overlay) return;
      // The hero video starts by itself as soon as it is on screen – show it as playing from the start (as before)
      const willAutoplay = video.hasAttribute('data-vbs-autoplay') && !reduceMotion.matches;
      const paused = video.paused && !willAutoplay;
      overlay.innerHTML = paused ? playSVG : pauseSVG;
      wrapper.classList.toggle('is-paused', paused);
      overlay.addEventListener('click', (e) => {
        e.stopPropagation();
        if (video.paused) playSafe(video); else video.pause();
      });
      video.addEventListener('play', () => { overlay.innerHTML = pauseSVG; wrapper.classList.remove('is-paused'); });
      video.addEventListener('pause', () => {
        if (video.vbsSelfPause) return; // paused only because it scrolled off screen
        overlay.innerHTML = playSVG; wrapper.classList.add('is-paused');
      });
    });

    // Sticky video: body.out-of-view while the hero is completely outside the viewport (until closed)
    const closeBtn = $(section, '.close-sticky-recipe');
    let isClosed = false;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        if (isClosed) return;
        entries.forEach((entry) => document.body.classList.toggle('out-of-view', !entry.isIntersecting));
      }).observe(section);
    }
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        document.body.classList.remove('out-of-view');
        isClosed = true;
      });
    }
  });

  // Portion buttons (onclick="updateRecipeScale(±1)") – recipe-content-body listens for recipePortionChanged
  window.updateRecipeScale = (change) => {
    const display = document.getElementById('p-count');
    if (!display) return;
    let newVal = parseInt(display.innerText, 10) + change;
    if (newVal < 1) newVal = 1;
    display.innerText = newVal + '×';   // UX review 10 Oct: »1×« = the recipe as written
    window.dispatchEvent(new CustomEvent('recipePortionChanged', { detail: { ratio: newVal / 1 } }));
  };

  /* ---------------- recipe-content-body (tick rows, scale ingredient amounts) ---------------- */
  define('rcb', (section) => {
    $$(section, '.js-toggle-row').forEach((row) => {
      row.addEventListener('click', () => row.classList.toggle('is-active'));
    });

    // Parse each amount once: leading number (digits , . /), an optional range end (»2–3«) + the rest of the text
    const qtyElements = $$(section, '.js-ing-qty');
    const toNum = (s) => {
      if (s.includes('/')) { const p = s.split('/'); return parseFloat(p[0]) / parseFloat(p[1]); }
      return parseFloat(s.replace(',', '.'));
    };
    // Danish decimal comma, two decimals at most (round 85c)
    const fmtNum = (n) => String((n % 1 === 0) ? n : Number(n.toFixed(2))).replace('.', ',');
    const FRACTIONS = { '½': 0.5, '¼': 0.25, '¾': 0.75 };
    qtyElements.forEach((el) => {
      const original = el.getAttribute('data-original-val') || el.innerText.trim();
      const match = original.match(/^([0-9.,\/]+)(?:\s?[–-]\s?([0-9.,]+))?(.*)$/);
      const fraction = original.match(/^(\d+)?\s?([½¼¾])(.*)$/);   // »½«, »1 ¼«
      el.setAttribute('data-clean-original', original);
      if (fraction) {
        el.setAttribute('data-clean-num', (fraction[1] ? parseFloat(fraction[1]) : 0) + FRACTIONS[fraction[2]]);
        el.setAttribute('data-clean-unit', fraction[3]);
      } else if (match) {
        el.setAttribute('data-clean-num', toNum(match[1].trim()));
        if (match[2]) el.setAttribute('data-clean-num2', toNum(match[2]));
        el.setAttribute('data-clean-unit', match[3]);
      } else {
        el.setAttribute('data-clean-num', 'NaN');
        el.setAttribute('data-clean-unit', original);
      }
    });

    window.addEventListener('recipePortionChanged', (e) => {
      const ratio = e.detail.ratio;
      qtyElements.forEach((el) => {
        const base = parseFloat(el.getAttribute('data-clean-num'));
        const base2 = parseFloat(el.getAttribute('data-clean-num2'));
        const unit = el.getAttribute('data-clean-unit');
        if (ratio === 1) {
          el.innerText = el.getAttribute('data-clean-original');   // 1× = the recipe exactly as written (»½«, »1 ¼«)
        } else if (!isNaN(base)) {
          el.innerText = fmtNum(base * ratio) + (!isNaN(base2) ? '–' + fmtNum(base2 * ratio) : '') + unit;
        } else {
          el.innerText = unit;
        }
      });
    });
  });

  /* ---------------- feature-product-list (product carousel + colour swatches) ---------------- */
  // Was Swiper 11 from jsdelivr (~150 KB JS + CSS, parser-blocking); now the shared scroll-snap carousel.
  define('fpl', (section) => {
    const root = $(section, '[data-vbs-carousel]');
    if (root) {
      const carousel = new Carousel(root, { pagination: $(root, '.js-fpl-dots') });
      const prevs = $$(section, '.js-fpl-prev');
      const nexts = $$(section, '.js-fpl-next');
      prevs.forEach((btn) => { btn.addEventListener('click', () => carousel.prev()); onKeyActivate(btn, () => carousel.prev()); });
      nexts.forEach((btn) => { btn.addEventListener('click', () => carousel.next()); onKeyActivate(btn, () => carousel.next()); });
      carousel.onChange((c) => {
        prevs.forEach((btn) => btn.classList.toggle('swiper-button-disabled', c.isBeginning));
        nexts.forEach((btn) => btn.classList.toggle('swiper-button-disabled', c.isEnd));
      });
    }

    // Colour swatch: swap card image + variant id of the add-to-cart form
    section.addEventListener('click', (event) => {
      const swatch = event.target.closest('.bs-swatch-element');
      if (!swatch) return;
      event.preventDefault();
      const card = swatch.closest('.bs-product-card');
      if (!card) return;
      const newImage = swatch.dataset.image;
      if (newImage) {
        card.querySelectorAll('.bs-main-img, .bs-hover-img').forEach((image) => {
          image.removeAttribute('srcset');
          image.src = newImage;
        });
      }
      const swatchList = swatch.closest('.bs-swatch-list');
      if (swatchList) swatchList.querySelectorAll('.bs-swatch-element').forEach((item) => item.classList.remove('is-active'));
      swatch.classList.add('is-active');
      const variantInput = card.querySelector('.bs-atc-form input[name="id"]');
      if (variantInput && swatch.dataset.variantId) variantInput.value = swatch.dataset.variantId;
    });
  });

  /* ---------------- recipe-slider (tabs with one carousel per tab) ---------------- */
  // Was Swiper 11 from jsdelivr + a global window.changeTab (broke with two of these sections on one page).
  define('rs', (wrapper) => {
    const carousels = new Map(); // pane -> Carousel
    const headPrev = $$(wrapper, '.js-rs-prev');
    const headNext = $$(wrapper, '.js-rs-next');
    const activePane = () => $(wrapper, '.vr-tab-pane.active');

    const syncHead = () => {
      const c = carousels.get(activePane());
      if (!c) return;
      headPrev.forEach((b) => b.classList.toggle('swiper-button-disabled', c.isBeginning));
      headNext.forEach((b) => b.classList.toggle('swiper-button-disabled', c.isEnd));
    };

    $$(wrapper, '.vr-tab-pane').forEach((pane) => {
      const root = $(pane, '[data-vbs-carousel]');
      if (!root) return;
      const c = new Carousel(root, { pagination: $(pane, '.js-rs-dots') });
      carousels.set(pane, c);
      const mPrev = $(pane, '.js-rs-mprev');
      const mNext = $(pane, '.js-rs-mnext');
      if (mPrev) { mPrev.addEventListener('click', () => c.prev()); onKeyActivate(mPrev, () => c.prev()); }
      if (mNext) { mNext.addEventListener('click', () => c.next()); onKeyActivate(mNext, () => c.next()); }
      c.onChange(() => {
        if (mPrev) mPrev.classList.toggle('swiper-button-disabled', c.isBeginning);
        if (mNext) mNext.classList.toggle('swiper-button-disabled', c.isEnd);
        if (pane.classList.contains('active')) syncHead();
      });
    });

    const step = (dir) => {
      const c = carousels.get(activePane());
      if (c) { if (dir > 0) c.next(); else c.prev(); }
    };
    headPrev.forEach((b) => { b.addEventListener('click', () => step(-1)); onKeyActivate(b, () => step(-1)); });
    headNext.forEach((b) => { b.addEventListener('click', () => step(1)); onKeyActivate(b, () => step(1)); });

    const vars = {
      bg: '--bg-section', heading: '--color-heading', sub: '--color-sub', tab: '--color-tab', arrow: '--color-arrow',
      cardTitle: '--color-card-title', meta: '--color-meta', headerBorder: '--header-border', b1Bg: '--badge-1-bg',
      b1Txt: '--badge-1-text', b2Bg: '--badge-2-bg', b2Txt: '--badge-2-text', ctaBg: '--cta-bg', ctaText: '--cta-text',
      ctaBorder: '--cta-border', ctaIcon: '--cta-icon',
    };
    $$(wrapper, '.vr-tab-btn').forEach((btn) => btn.addEventListener('click', () => {
      $$(wrapper, '.vr-tab-btn').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      $$(wrapper, '.vr-tab-pane').forEach((p) => p.classList.remove('active'));
      const pane = wrapper.querySelector(`.vr-tab-pane[id="tab-${btn.dataset.tabId}"]`);
      if (pane) pane.classList.add('active');
      Object.keys(vars).forEach((k) => wrapper.style.setProperty(vars[k], btn.dataset[k]));
      const c = carousels.get(pane);
      if (c) c.update();
      syncHead();
    }));
  });

  /* ---------------- custom-blog-section (hover a guide card -> preview it on the left) ---------------- */
  define('cbs', (section) => {
    const mediaContainer = $(section, '#mainMediaContainer');
    const titleEl = $(section, '#mainTitle');
    const descEl = $(section, '#mainDesc');
    if (!mediaContainer || !titleEl || !descEl) return;
    const esc = (s) => String(s || '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

    $$(section, '.viretta-card').forEach((card) => {
      card.addEventListener('mouseenter', () => {
        const videoUrl = card.dataset.video;
        const imageUrl = card.dataset.image;
        const title = card.dataset.title;
        titleEl.innerText = title;
        descEl.innerText = card.dataset.desc;
        if (videoUrl && videoUrl.trim() !== '') {
          mediaContainer.innerHTML = `
          <div class="media-wrapper">
            <video autoplay muted loop playsinline style="width:100%; height:100%; object-fit:cover;">
              <source src="${esc(videoUrl)}" type="video/mp4">
            </video>
          </div>`;
          const vid = mediaContainer.querySelector('video');
          if (vid) playSafe(vid);
        } else {
          mediaContainer.innerHTML = `
          <div class="media-wrapper">
            <img src="${esc(imageUrl)}" alt="${esc(title)}" style="width:100%; height:100%; object-fit:cover;">
          </div>`;
        }
      });
    });
  });

  /* ---------------- guide sliders: custom-blog-slider-guide, custom-guide-article-slider,
                      secondary-blog-slider-guide, use-guides-grid (native scroll + dots) ---------------- */
  const simpleSlider = (root, cfg) => {
    const slider = $(root, cfg.track);
    if (!slider) return;
    const cards = $$(slider, cfg.card);
    const dotsContainer = $(root, cfg.dots);
    if (!dotsContainer) return;
    if (cfg.hideIfSingle && cards.length <= 1) {
      cfg.hideIfSingle.forEach((sel) => { const el = $(root, sel); if (el) el.style.display = 'none'; });
      return;
    }
    if (!cards.length) return;
    const gap = () => parseInt(window.getComputedStyle(slider).gap, 10) || cfg.gapFallback;
    cards.forEach((card, index) => {
      const dot = document.createElement('button');
      dot.classList.add(cfg.dotClass);
      dot.setAttribute('aria-label', `Bild ${index + 1} anzeigen`);
      if (index === 0) dot.classList.add(cfg.activeClass);
      dot.addEventListener('click', () => {
        slider.scrollTo({ left: (cards[cfg.dotUsesFirst ? 0 : index].offsetWidth + gap()) * index, behavior: 'smooth' });
      });
      dotsContainer.appendChild(dot);
    });
    const dots = $$(dotsContainer, `.${cfg.dotClass}`);
    const step = () => cards[0].offsetWidth + gap();
    $$(root, cfg.prev).forEach((btn) => btn.addEventListener('click', () => slider.scrollBy({ left: -step(), behavior: 'smooth' })));
    $$(root, cfg.next).forEach((btn) => btn.addEventListener('click', () => slider.scrollBy({ left: step(), behavior: 'smooth' })));
    slider.addEventListener('scroll', () => {
      const i = Math.round(slider.scrollLeft / step());
      dots.forEach((dot, n) => dot.classList.toggle(cfg.activeClass, n === i));
    }, { passive: true });
  };

  define('cbsg', (root) => simpleSlider(root, {
    track: '.c-blog-slider', card: '.c-blog-card', dots: '.c-slider-dots', prev: '.c-prev, .c-prev-m', next: '.c-next, .c-next-m',
    dotClass: 'c-dot', activeClass: 'active', gapFallback: 0,
  }));

  define('cgas', (root) => simpleSlider(root, {
    track: '.tbs-slider', card: '.tbs-card', dots: '.tbs-slider-dots', prev: '.tbs-prev, .tbs-prev-m', next: '.tbs-next, .tbs-next-m',
    dotClass: 'tbs-dot', activeClass: 'active', gapFallback: 0,
  }));

  define('sbsg', (root) => {
    // click-to-play videos (controls while playing, reset at the end)
    $$(root, '.js-guide-play').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const vid = btn.previousElementSibling;
        if (vid && vid.tagName === 'VIDEO') {
          vid.setAttribute('controls', 'true');
          playSafe(vid);
          btn.style.display = 'none';
          vid.addEventListener('ended', () => {
            vid.removeAttribute('controls');
            vid.currentTime = 0;
            btn.style.display = 'flex';
          }, { once: true });
        }
      });
    });
    simpleSlider(root, {
      track: '.guide-carousel-track', card: '.guide-card', dots: '.guide-pagination-dots',
      prev: '[class*="js-guide-prev-"]', next: '[class*="js-guide-next-"]',
      dotClass: 'guide-dot', activeClass: 'guide-dot-active', gapFallback: 12, dotUsesFirst: true,
      hideIfSingle: ['.guide-nav-mobile', '.guide-nav-desktop'],
    });
  });

  const mutedSVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line></svg>';
  const unmutedSVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>';

  define('ugg', (root) => {
    $$(root, '.js-ugb-mute').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        const vid = btn.parentElement.querySelector('.js-ugb-video');
        if (!vid) return;
        vid.muted = !vid.muted;
        btn.innerHTML = vid.muted ? mutedSVG : unmutedSVG;
      });
    });
    $$(root, '.js-ugb-play').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const vid = btn.parentElement.querySelector('.js-ugb-video');
        if (vid) { playSafe(vid); btn.style.display = 'none'; }
      });
    });
    $$(root, '.js-ugb-video').forEach((vid) => {
      vid.addEventListener('click', () => {
        if (!vid.paused) {
          vid.pause();
          const playBtn = vid.parentElement.querySelector('.js-ugb-play');
          if (playBtn) playBtn.style.display = 'flex';
        }
      });
      vid.addEventListener('ended', () => {
        vid.currentTime = 0;
        const playBtn = vid.parentElement.querySelector('.js-ugb-play');
        if (playBtn) playBtn.style.display = 'flex';
      });
    });
    simpleSlider(root, {
      track: '.ugb-slider-track', card: '.ugb-card', dots: '.ugb-dots',
      prev: '[class*="js-ugb-prev-"]', next: '[class*="js-ugb-next-"]',
      dotClass: 'ugb-dot', activeClass: 'active', gapFallback: 12, dotUsesFirst: true,
      hideIfSingle: ['.ugb-nav-mobile', '.ugb-nav-desktop'],
    });
  });

  /* ---------------- article-content (guide article: [product] card add-to-cart, share / copy link) ---------------- */
  define('ac', (root) => {
    // [product] shortcode card: add to cart, then go to the cart
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('.js-add-to-cart');
      if (!btn) return;
      fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: btn.getAttribute('data-id'), quantity: 1 }] }),
      })
        .then((r) => r.json())
        .then(() => { window.location.href = '/cart'; });
    });
  });

  // Share buttons (onclick="copyBlogUrl()" / "shareToInstagram()") – clean URL without query string
  const shareUrl = () => `${window.location.protocol}//${window.location.hostname}${window.location.pathname}`;
  const showToast = () => {
    const popup = document.getElementById('vCopyPopup');
    if (!popup) return;
    popup.classList.add('is-visible');
    setTimeout(() => popup.classList.remove('is-visible'), 2000);
  };
  window.copyBlogUrl = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(shareUrl()).then(showToast);
  };
  window.shareToInstagram = () => {
    window.copyBlogUrl();
    const url = shareUrl();
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isiOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isiOS) {
      window.location.href = `instagram://sharesheet?text=${encodeURIComponent(url)}`;
      setTimeout(() => { if (!document.hidden) window.location.href = 'https://www.instagram.com/direct/inbox/'; }, 1000);
    } else if (isAndroid) {
      if (navigator.share) {
        navigator.share({ text: url }).catch(() => { window.location.href = 'instagram://direct_share'; });
      } else {
        window.location.href = 'instagram://direct_share';
      }
    } else {
      window.open('https://www.instagram.com/direct/inbox/', '_blank');
    }
  };

  /* ---------------- reveal-on-scroll helper (Blog-watch-cook, collection-banner-grid, rich-text-custom) ---------------- */
  // Same trigger as before: when the element passes the line 70 % down the viewport.
  const revealOnce = (target, onShow) => {
    if (!('IntersectionObserver' in window)) { onShow(); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { onShow(); io.unobserve(target); }
      });
    }, { root: null, threshold: 0, rootMargin: '-70% 0px -30% 0px' });
    io.observe(target);
  };

  /* ---------------- Blog-watch-cook (image/video + text from the article metaobject) ---------------- */
  define('bwc', (section) => {
    revealOnce(section, () => {
      $$(section, '.animate-text-image, .animate-content').forEach((el) => el.classList.add('active'));
    });
    // Play/pause toggle (was a page-wide querySelector that threw on articles without a video)
    const video = $(section, 'video.fixed-imaginery');
    const toggle = $(section, '.play-toggle');
    if (!video || !toggle) return;
    const iconPlay = $(toggle, '.icon-play');
    const iconPause = $(toggle, '.icon-pause');
    const show = (playing) => {
      if (iconPlay) iconPlay.style.display = playing ? 'none' : 'block';
      if (iconPause) iconPause.style.display = playing ? 'block' : 'none';
    };
    video.pause();
    show(false);
    toggle.addEventListener('click', () => {
      if (video.paused) { playSafe(video); show(true); } else { video.pause(); show(false); }
    });
  });

  /* ---------------- collection-banner-grid (two banners slide in) ---------------- */
  define('cbg', (section) => {
    revealOnce(section, () => {
      $$(section, '.collection__banner-grid-banner-left, .collection__banner-grid-banner-right').forEach((el) => el.classList.add('active'));
    });
  });

  /* ---------------- rich-text-custom (heading fades in) ---------------- */
  // Was a top-level `const observer` in an inline script – a second instance on the same page threw
  // "Identifier 'observer' has already been declared". Like before it watches every .animate-heading on the page.
  const rtcSeen = new WeakSet();
  define('rtc', () => {
    $$(document, '.animate-heading').forEach((el) => {
      if (rtcSeen.has(el)) return;
      rtcSeen.add(el);
      if (!('IntersectionObserver' in window)) { el.classList.add('active'); return; }
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) entry.target.classList.add('active'); });
      }, { root: null, threshold: 0, rootMargin: '-70% 0px -30% 0px' }).observe(el);
    });
  });

  /* ---------------- recipe cards: emoji reactions + »Gem opskrift« (snippets/vbs-reactions.liquid, [data-vbs-save]) ---------------- */
  // Owner 9 Oct: »customers can add an emoji style response by clicking and then it counts up«.
  // One reaction per visitor per recipe (localStorage »vbs-react«); click the same emoji again to remove it, another to change.
  // Shared counts come from the counter service in settings.vbs_reactions_url (workspace/reactions-worker):
  //   GET  <url>?ids=a,b  -> { counts: { a: { "👍": 3, "❤️": 1 }, … } }
  //   POST <url> { id, emoji, prev } -> { counts: { "👍": 4, … } }   (emoji "" = remove)
  // Without the URL the visitor sees the start number plus their own reaction. Saved recipes: localStorage »vbs-saved«.
  const store = {
    get(key, fallback) { try { const v = JSON.parse(localStorage.getItem(key)); return v == null ? fallback : v; } catch (e) { return fallback; } },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ } }
  };
  const EMOJIS = ['👍', '❤️', '😋', '🔥'];
  const mine = () => store.get('vbs-react', {});
  const server = {}; // id -> counts from the counter service
  const sum = (o) => Object.values(o || {}).reduce((a, n) => a + (Number(n) || 0), 0);

  const renderReact = (el) => {
    const id = el.dataset.vbsReact;
    const base = Number(el.dataset.base) || 0;
    const my = mine()[id] || '';
    const counts = Object.assign({}, server[id] || {});
    const hasServer = !!server[id];
    if (!hasServer && my) counts[my] = (counts[my] || 0) + 1; // own reaction until the service is connected
    const total = base + sum(counts);
    el.querySelectorAll('[data-vbs-react-count]').forEach((c) => { c.textContent = total; });
    const top = el.querySelector('[data-vbs-react-top]');
    if (top) {
      const order = EMOJIS.slice().sort((a, b) => (counts[b] || 0) - (counts[a] || 0));
      const pick = (counts[order[0]] ? order : ['👍', '❤️']).slice(0, 2);
      if (my && !pick.includes(my)) pick[1] = my;
      top.querySelectorAll('.vr-reaction-circle').forEach((s, i) => { s.textContent = pick[i] || ''; });
    }
    el.classList.toggle('is-reacted', !!my);
    el.querySelectorAll('.vbs-react__picker [data-e]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.e === my)));
    const btn = el.querySelector('.vbs-react__btn');
    if (btn) btn.setAttribute('aria-label', my ? `Deine Reaktion: ${my} – ändern oder entfernen` : 'Auf das Rezept reagieren');
  };

  const closePickers = (except) => {
    $$(document, '.vbs-react__picker:not([hidden])').forEach((p) => {
      if (p === except) return;
      p.hidden = true;
      const b = p.parentElement.querySelector('.vbs-react__btn');
      if (b) b.setAttribute('aria-expanded', 'false');
    });
  };

  const endpointFor = (el) => el.dataset.endpoint || '';
  const fetchCounts = (scope) => {
    const els = $$(scope, '[data-vbs-react]');
    const byUrl = {};
    els.forEach((el) => { const u = endpointFor(el); if (u && !server[el.dataset.vbsReact]) (byUrl[u] = byUrl[u] || new Set()).add(el.dataset.vbsReact); });
    Object.keys(byUrl).forEach((u) => {
      const ids = Array.from(byUrl[u]);
      for (let i = 0; i < ids.length; i += 40) {
        const part = ids.slice(i, i + 40);
        fetch(`${u}${u.includes('?') ? '&' : '?'}ids=${encodeURIComponent(part.join(','))}`, { credentials: 'omit' })
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            if (!data || !data.counts) return;
            part.forEach((id) => { server[id] = data.counts[id] || {}; });
            $$(document, '[data-vbs-react]').forEach((el) => { if (part.includes(el.dataset.vbsReact)) renderReact(el); });
          })
          .catch(() => {});
      }
    });
  };

  const react = (el, emoji) => {
    const id = el.dataset.vbsReact;
    const all = mine();
    const prev = all[id] || '';
    const next = prev === emoji ? '' : emoji;
    if (next) all[id] = next; else delete all[id];
    store.set('vbs-react', all);
    if (server[id]) { // optimistic update of the shared numbers
      if (prev) server[id][prev] = Math.max(0, (server[id][prev] || 0) - 1);
      if (next) server[id][next] = (server[id][next] || 0) + 1;
    }
    $$(document, '[data-vbs-react]').forEach((x) => { if (x.dataset.vbsReact === id) renderReact(x); });
    const u = endpointFor(el);
    if (u) {
      fetch(u, { method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, emoji: next, prev }) })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => { if (data && data.counts) { server[id] = data.counts; $$(document, '[data-vbs-react]').forEach((x) => { if (x.dataset.vbsReact === id) renderReact(x); }); } })
        .catch(() => {});
    }
  };

  const saved = () => store.get('vbs-saved', []);
  const renderSave = (el) => {
    const on = saved().includes(el.dataset.vbsSave);
    el.classList.toggle('is-saved', on);
    el.setAttribute('aria-pressed', String(on));
    if (el.hasAttribute('aria-label')) el.setAttribute('aria-label', on ? 'Aus meinen Rezepten entfernen' : 'Rezept speichern');
    const label = el.querySelector('[data-vbs-save-label]');
    if (label) label.textContent = on ? label.dataset.on : label.dataset.off;
  };

  // Cards are links: stop the card from navigating when a reaction or save control is used
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!t.closest) return;
    const saveEl = t.closest('[data-vbs-save]');
    const reactEl = t.closest('[data-vbs-react]');
    if (!saveEl && !reactEl) { closePickers(); return; }
    e.preventDefault();
    e.stopPropagation();
    if (saveEl) {
      const id = saveEl.dataset.vbsSave;
      const list = saved();
      const i = list.indexOf(id);
      if (i >= 0) list.splice(i, 1); else list.push(id);
      store.set('vbs-saved', list);
      $$(document, '[data-vbs-save]').forEach((x) => { if (x.dataset.vbsSave === id) renderSave(x); });
      return;
    }
    const choice = t.closest('.vbs-react__picker [data-e]');
    const picker = reactEl.querySelector('.vbs-react__picker');
    if (choice) { react(reactEl, choice.dataset.e); picker.hidden = true; reactEl.querySelector('.vbs-react__btn').setAttribute('aria-expanded', 'false'); return; }
    if (t.closest('.vbs-react__btn, [data-vbs-react-top], [data-vbs-react-count]')) {
      const open = picker.hidden;
      closePickers(picker);
      picker.hidden = !open;
      reactEl.querySelector('.vbs-react__btn').setAttribute('aria-expanded', String(open));
      if (open) { const first = picker.querySelector('[aria-pressed="true"]') || picker.querySelector('[data-e]'); if (first) first.focus({ preventScroll: true }); }
    }
  }, true);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closePickers();
    const t = e.target;
    if ((e.key === 'Enter' || e.key === ' ') && t.matches && t.matches('[data-vbs-save][role="button"]')) { e.preventDefault(); t.click(); }
  });

  const applyCards = (scope) => {
    $$(scope, '[data-vbs-react]').forEach(renderReact);
    $$(scope, '[data-vbs-save]').forEach(renderSave);
    fetchCounts(scope);
  };
  const bootCards = () => {
    applyCards(document);
    if ('MutationObserver' in window) {
      new MutationObserver((list) => {
        list.forEach((m) => m.addedNodes.forEach((n) => {
          if (n.nodeType === 1 && (n.matches('[data-vbs-react], [data-vbs-save]') || n.querySelector('[data-vbs-react], [data-vbs-save]'))) applyCards(n.parentElement || document);
        }));
      }).observe(document.body, { childList: true, subtree: true });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootCards); else bootCards();


  /* ------------------------------------------------------------------ boot */
  const scan = (root) => {
    const scope = root || document;
    const roots = scope.matches && scope.matches('[data-vbs]') ? [scope, ...$$(scope, '[data-vbs]')] : $$(scope, '[data-vbs]');
    roots.forEach((el) => {
      el.vbsDone = el.vbsDone || {};
      el.dataset.vbs.split(/\s+/).forEach((name) => {
        if (!name || el.vbsDone[name] || !inits[name]) return;
        el.vbsDone[name] = true;
        try { inits[name](el); } catch (err) { if (window.console) console.error('vbs ' + name, err); }
      });
    });
    $$(scope, 'video[data-vbs-autoplay]').forEach(observeAutoplay);
  };

  window.VbsSections = { scan, Carousel, define };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document));
  else scan(document);
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
})();
